import { SortDirection } from "@/Common/Models/UI/Enums";
import { ITableDataSourceItem } from "@/Common/Models/UI/Tables/ITableDataSourceItem";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";

import style from "./MyleTableColumn.module.scss";

import TableDownArrow from "./_images/icons/table-downarrow.svg";
import TableUpArrow from "./_images/icons/table-uparrow.svg";

class ColumnClickArgs {
    public target: MyleTableTarget;
    public item: MyleTableColumn;

    public constructor(init: Partial<ColumnClickArgs> = null) {
        Object.assign(this, init);
    }
}

class MyleTableColumnOptions {
    public HeaderText: string;
    public FieldName: string;
    public RowClickEnabled = false;
    public IsSortable = false;
    public SortColumn: number = null;
    public SortDirection = SortDirection.DESC;
    public IsSortActive = false;
    public Id: string;
    public WidthPercentage = 0;
    public WidthPercentageDefault = 0;
    public WidthPixels: number;
    public IsHidden = false;
    public IsFixedInPlace = false;
    public IsStaticWidth = false;
    public DefaultWrapBehavior = false;
    public columnClass = "";
    public FloatLeft = false;
    public DataCy?: string;
    public sortingMethod?: () => void;

    constructor(fields?: Partial<MyleTableColumnOptions>) {
        (Object as any).assign(this, fields);
    }

    public static DefaultFixed(Header: string, Name: string, pixelWidth: number, EnableRowClick: boolean, SortColomn: number = NaN, sortDirection: SortDirection = SortDirection.ASC): MyleTableColumnOptions {
        let tmpTableColumn = new MyleTableColumnOptions();
        tmpTableColumn.HeaderText = Header;
        tmpTableColumn.FieldName = Name;
        tmpTableColumn.WidthPixels = pixelWidth;
        tmpTableColumn.IsFixedInPlace = true;
        tmpTableColumn.RowClickEnabled = EnableRowClick;
        tmpTableColumn.IsSortable = SortColomn != null && !isNaN(SortColomn);
        tmpTableColumn.SortColumn = SortColomn;
        tmpTableColumn.SortDirection = sortDirection;
        return tmpTableColumn;
    }

    public static Default(Header: string, Name: string, widthPercent: number, EnableRowClick: boolean, SortColomn: number = NaN, sortDirection: SortDirection = SortDirection.ASC): MyleTableColumnOptions {
        let tmpTableColumn = new MyleTableColumnOptions();
        tmpTableColumn.HeaderText = Header;
        tmpTableColumn.FieldName = Name;
        tmpTableColumn.WidthPercentageDefault = widthPercent;
        tmpTableColumn.RowClickEnabled = EnableRowClick;
        tmpTableColumn.IsSortable = SortColomn != null && !isNaN(SortColomn);
        tmpTableColumn.SortColumn = SortColomn;
        tmpTableColumn.SortDirection = sortDirection;
        return tmpTableColumn;
    }
}

/**
 * The fields which can be used to construct a Myle table column.
 */
class MyleTableColumnInit<TItem extends ITableDataSourceItem<any>> {
    /**
     * An optional function which renders the table header cell for this column. This receives an optional delegate to call when the header cell is clicked by the user. If omitted, the default header cell will be rendered instead.
     */
    public renderHeader?: (clickDelegate?: ColumnClickArgs) => JSX.Element;

    /**
     * A function which renders the table cell for a row in the table. This receives the item data for the row.
     */
    public renderCell: (rowData: TItem) => JSX.Element;

    /**
     * The local operation code to dispatch when the column is sorted.
     */
    public columnSortEventId?: number;
}

class MyleTableColumn {
    public columnOptions: MyleTableColumnOptions;

    /**
     * The function which renders the table header cell for this column, if any. This receives an optional delegate to call when the header cell is clicked by the user.
     */
    public renderHeader: (clickDelegate?: ColumnClickArgs) => JSX.Element;

    /**
     * The function which renders the table cell for a row in the table. This receives the item data for the row.
     */
    public renderCell: (rowData: ITableDataSourceItem<any>) => JSX.Element;

    /**
     * The local operation code to dispatch when the column is sorted.
     */
    public columnSortEventId?: number;

    constructor(options: MyleTableColumnOptions, init?: MyleTableColumnInit<any>) {
        this.columnOptions = options;
        this.ResetHeader();

        this.renderCell = init?.renderCell ?? this.renderCell;
        this.renderHeader = init?.renderHeader ?? this.renderHeader;
        this.columnSortEventId = init?.columnSortEventId ?? LocalOperations.Misc.Table_ColumnSortClick;
    }

    public ToggleSortDirection(): void {
        if (this.columnOptions.SortDirection === SortDirection.ASC) {
            this.columnOptions.SortDirection = SortDirection.DESC;
        }
        else if (this.columnOptions.SortDirection === SortDirection.DESC) {
            this.columnOptions.SortDirection = SortDirection.ASC;
        }
    }

    public ResetHeader(): void {
        this.renderHeader ??= (clickDelegate?: ColumnClickArgs) => {
            const sortArrow: JSX.Element = this.columnOptions.IsSortActive
                ? (
                    <div className={style.sortIcon}>
                        {this.columnOptions.SortDirection === SortDirection.ASC ? <TableUpArrow /> : <TableDownArrow />}
                    </div>
                )
                : null;

            return clickDelegate
                ? (<div><a className={style.columnClickableHeader} onClick={e => this.onColumnClick(clickDelegate, e)}>{this.columnOptions.HeaderText}{sortArrow}</a></div>)
                : (<div><a className={style.columnClickableHeader}>{this.columnOptions.HeaderText}{sortArrow}</a></div>);
        };
    }

    private onColumnClick(sender: ColumnClickArgs, e: React.MouseEvent): void {
        if (!sender.item.columnOptions.IsSortable)
            return;

        if (!sender.item.columnOptions.sortingMethod) {
            e.stopPropagation();

            sessionStore.dispatch(this.columnSortEventId, {
                target: sender.target,
                sortColumn: sender.item.columnOptions.SortColumn,
                columnObject: sender.item
            });
        }
        else {
            sender.item.columnOptions.sortingMethod();
        }
    }

    public Clone(): MyleTableColumn {
        return new MyleTableColumn(this.columnOptions, this);
    }

    /**
     * Build a list of Myle table columns.
     * @param columnOptions The column options to render into a table.
     * @param getInit Get the field values with which to construct a column.
     * @param filterHidden Whether to hide columns marked with {@link MyleTableColumnOptions.IsHidden}.
     */
    public static buildList<TItem extends ITableDataSourceItem<any>>(columnOptions: Immutable.List<MyleTableColumnOptions>, getInit: (options: MyleTableColumnOptions) => MyleTableColumnInit<TItem> | null, filterHidden: boolean = true): Immutable.List<MyleTableColumn> {
        return columnOptions
            .filter(column => !column.IsHidden || !filterHidden)
            .map(column => new MyleTableColumn(column, getInit(column)))
            .toList();
    }
}

export { ColumnClickArgs, MyleTableColumn, MyleTableColumnInit, MyleTableColumnOptions };
